import { Moment } from 'moment'
import { CxIntegrationLicence } from '../../NonReact/common/Types/SelfProvisioning/SelfProvisioningTypes'
import { ReportingAccessLevels } from '../../NonReact/constants/Permissions'

export type UserRoleMetadata = {
    TotalCustomRoleSlots: number
    UsedCustomRoleSlots: number
}

export type UserRoleResponse = {
    Data: UserRoleItemWrapper
    Metadata: UserRoleMetadata
}

export type UserRoleItemWrapper = {
    Items: UserRole[]
}

export type UserRole = {
    Name: string
    RoleID: string
    Description: string
    RequiresReporting: boolean
    Permissions: string[]
    IsCustomizedRole: boolean
}

export type RolePermissions = {
    Read: boolean
    Create: boolean
    Update: boolean
    Delete: boolean
    Allowed: boolean
}

export type FormRolePermissions = {
    devices?: RolePermissions
    agents?: RolePermissions
    directory?: RolePermissions
    applicationUser?: RolePermissions
    codes?: RolePermissions
    telServer?: RolePermissions
    paritions?: RolePermissions
    recordings?: RolePermissions
    cmeManagement?: RolePermissions
    messages?: RolePermissions
    database?: RolePermissions
    logManagement?: RolePermissions
    callControl?: RolePermissions
    licences?: RolePermissions
    logging?: RolePermissions
    auditTrail?: RolePermissions
    telModuleStatus?: RolePermissions
    omnichannelStatus?: RolePermissions
    serverStatus?: RolePermissions
    copyReportsTo?: RolePermissions
    signInAs?: RolePermissions
    billingFilesDownload?: RolePermissions
    billSummary?: RolePermissions
    changeLicenceLevel?: RolePermissions
    syncpartition?: RolePermissions
    eptAccess?: RolePermissions
    TelSysSyncRestrictions?: RolePermissions
}

export enum APIKeyStatus {
    ACTIVE = 'active',
    EXPIRED = 'expired'
}

export type APIKeysInfoBackend = {
    ID?: string
    Name: string
    Status: APIKeyStatus
    DateTimeExpiry: Nullable<number>
    DateTimeCreated: Nullable<number>
    DateTimeLastUsed: Nullable<number>
    APIKey?: string
}

export type APIKeysInfo = {
    id?: string
    name: string
    status: APIKeyStatus
    dateTimeExpiry: Nullable<number>
    dateTimeCreated: Nullable<number>
    dateTimeLastUsed: Nullable<number>
    apiKey?: string
}

export type APIToken = {
    id?: string
    name?: string
    token?: string
}

export type APIKeyForm = {
    name?: string
    expiry?: Nullable<Moment>
}

export type APIKeyPOST = {
    Name: string
    DateTimeExpiry: Nullable<number>
}

export type ApplicationUserFormData = {
    role?: {
        role?: string
    }
    administrator?: {
        rolepermissions?: FormRolePermissions
    }
    details?: {
        fullname?: string
        username?: string
        email?: string
        password?: string
        passwordchangerequired?: boolean
        defaultlanguage?: string
        sendwelcomeemail?: boolean
        enablemfa?: boolean
        cxilicence: CxIntegrationLicence
        cxidataentitlement: boolean
        timezone: string
        cxsSubscription?: string
        cxsScoreEnabled?: boolean
        cxrSubscription?: string
        cxrRecordEnabled?: boolean
    }
    settings?: {
        updateintervals?: string
        lockedout?: boolean
        prompttos?: boolean
        notifypasswordchange?: boolean
        disablemediaplayer?: boolean
        draganddropfunctionality?: boolean
        javascriptoutput?: boolean
        concurrentsignins?: boolean
        servicenotification?: boolean
        authnotifications?: boolean
        newdevicenotifications?: boolean
    }
    permissions?: {
        cmenvironment?: string
        telephonyserver?: number
        partition?: number[]
        device?: string
        agent?: string
        synchronisesupervisorpermissions?: boolean
    }
    extensionendpoint?: {
        device?: string
        agent?: string
        telephonyserver?: number
        partition?: number
    }
    reporting?: {
        reportinglevel?: string
        defaultreports?: string
        callrecording?: string
        readonly?: boolean
        enhancedfeatures?: boolean
        reportingapi?: boolean
        reportTemplateUser?: boolean
    }
    apiKeys?: APIKeyForm
}

/**
 * Optional admin-list (or parent UI) scope passed into add-user so permissions defaults match the caller.
 */
export type FilterValues = {
    cmeID?: string
    telSysID?: number
    partitionID?: number
    /**
     * When true, telephony servers are loaded for the selected CME with the same recursion as the admin users list
     * ({@code includeCmeChildren}), so TelSys options include servers under descendant CMEs, not only direct children.
     */
    includeCmeChildren?: boolean
}

export type InitialApplicationUserValues = {
    role?: Partial<NonNullable<ApplicationUserFormData['role']>>
    administrator?: Partial<NonNullable<ApplicationUserFormData['administrator']>>
    details?: Partial<NonNullable<ApplicationUserFormData['details']>>
    settings?: Partial<NonNullable<ApplicationUserFormData['settings']>>
    permissions?: Partial<NonNullable<ApplicationUserFormData['permissions']>>
    extensionendpoint?: Partial<NonNullable<ApplicationUserFormData['extensionendpoint']>>
    reporting?: Partial<NonNullable<ApplicationUserFormData['reporting']>>
    apiKeys?: Partial<NonNullable<ApplicationUserFormData['apiKeys']>>
}

export type PreferredReportingAccessLevel = ReportingAccessLevels.ESSENTIALS | ReportingAccessLevels.ONE

export type PartitionValues = {
    LicensedForRptUserType: string
    Description: string
    PartIDInTelSys: number
    SynchroniseSupervisorPermissions: boolean
    TimeZone: string
    CallRecordingIDDefault?: string
}

export type Capabilities = {
    legacy: boolean
    enterprise: boolean
    cxIntegration: boolean
}
